import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Form, Input, Popconfirm, Table, Modal, App } from 'antd';
import { Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AxiosCaller from '../../../authenticatie/AxiosCaller';
import { openNotificationSuccess, openNotificationError } from '../../../Components/Notification/Notification';

// Create the editable context
const EditableContext = React.createContext(null);

// EditableRow component
const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

// EditableCell component
const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.error('Save failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[{ required: true, message: `${title} is required.` }]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div className="editable-cell-value-wrap" style={{ paddingInlineEnd: 24 }} onClick={toggleEdit}>
        {children}
      </div>
    );
  }
  return <td {...restProps}>{childNode}</td>;
};

const BestelTermijnScreen = () => {
  const { get, post, put, del } = AxiosCaller();
  const [besteltermijnen, setBesteltermijnen] = useState([]);
  const [loading, setLoading] = useState(true);

 
  const [selectedBestelTermijn, setSelectedBestelTermijn] = useState(null);
  const [newBestelTermijnIndex, setNewBestelTermijnIndex] = useState(null);
  const tableRef = useRef(null);

  const navigate = useNavigate();

  // Fetch besteltermijnen on component mount
  useEffect(() => {
    fetchBesteltermijnen();
  }, []);

  // API call to fetch besteltermijnen
  const fetchBesteltermijnen = async () => {
    try {
      const response = await get('/besteltermijn');
      if (Array.isArray(response.data)) {
        setBesteltermijnen(response.data);
      } else {
        console.error('API returned non-array data:', response.data);
        setBesteltermijnen([]);
        openNotificationError("Foute data opgehaalt")
      }
    } catch (error) {
      console.error('Error fetching besteltermijnen:', error);
      openNotificationError("Fout bij ophalen van besteltermijnen", error)
    } finally {
      setLoading(false);
    }
  };


  // Handle add new besteltermijn
  const handleAdd = async () => {
    try {
      const newBestelTermijn = { naam: 'Nieuwe Besteltermijn' };
      const response = await post('/besteltermijn', newBestelTermijn);
      setBesteltermijnen([response.data, ...besteltermijnen]);
      setNewBestelTermijnIndex(0);
    } catch (error) {
      openNotificationError("Fout bij toevoegen besteltermijn", error)
      console.error('Error adding besteltermijn:', error);
    }
  };

  // Handle delete besteltermijn
  const handleDelete = async (id) => {
    try {
      await del(`/besteltermijn/${id}`);
      await fetchBesteltermijnen()
      openNotificationSuccess("besteltermijn succesvol verwijderd!")
    } catch (error) {
      openNotificationError("Fout bij verwijderen van besteltermijn", error)
      console.error('Error deleting besteltermijn:', error);
     
    }
  };

  // Handle save (edit) besteltermijn
  const handleSave = async (row) => {
    try {
      await put(`/besteltermijn/${row._id}`, { naam: row.naam });
      const newData = besteltermijnen.map(item => (item._id === row._id ? row : item));
      setBesteltermijnen(newData);
    } catch (error) {
      openNotificationError("Fout bij aanpassen van besteltermijn",error)
      console.error('Error updating besteltermijn:', error);
    }
  };


  // Table columns configuration
  const columns = [
    {
      title: 'Naam',
      dataIndex: 'naam',
      editable: true,
      sorter: (a, b) => a.naam.localeCompare(b.naam),
    },
    {
      title: 'Acties',
      dataIndex: 'operation',
      width: '15%',
      render: (_, record) => (
        <Popconfirm 
          title="Weet je zeker dat je deze besteltermijn wilt verwijderen?"
          onConfirm={() => handleDelete(record._id)}
        >
          <Trash2 className='delete-button' />
        </Popconfirm>
      ),
    },
  ];

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columnConfig = columns.map(col => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: record => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  return (
    <div className='Screen-container'>
      <div className='space-between'>
        <h1 className='title'>Besteltermijn</h1>
        <Button onClick={handleAdd} type="primary" className='button'>
          <Plus /> Nieuwe besteltermijn
        </Button>
      </div>

    

      <Table
        ref={tableRef}
        components={components}
        rowClassName={() => 'editable-row'}
        bordered
        dataSource={besteltermijnen.map(item => ({ ...item, key: item._id }))}
        columns={columnConfig}
        pagination={false}
        scroll={{ y: 0.5 * window.innerHeight }}
        loading={loading}
      />

     
    </div>
  );
};

export default BestelTermijnScreen;